import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import supabaseService from '../services/SupabaseService';
import { useAuth } from '../contexts/AuthContext';
import firebaseService from '../services/FirebaseService';

// Board position metadata: name + which side (radiant/dire) + is ban
const SLOT_META = {
  1:  { name: 'R Ban 1',   team: 'radiant', isBan: true  },
  2:  { name: 'R Ban 2',   team: 'radiant', isBan: true  },
  3:  { name: 'D Ban 1',   team: 'dire',    isBan: true  },
  4:  { name: 'D Ban 2',   team: 'dire',    isBan: true  },
  5:  { name: 'R Ban 3',   team: 'radiant', isBan: true  },
  6:  { name: 'D Ban 3',   team: 'dire',    isBan: true  },
  7:  { name: 'D Ban 4',   team: 'dire',    isBan: true  },
  8:  { name: 'R Pick 1',  team: 'radiant', isBan: false },
  9:  { name: 'D Pick 1',  team: 'dire',    isBan: false },
  10: { name: 'R Ban 4',   team: 'radiant', isBan: true  },
  11: { name: 'R Ban 5',   team: 'radiant', isBan: true  },
  12: { name: 'D Ban 5',   team: 'dire',    isBan: true  },
  13: { name: 'D Pick 2',  team: 'dire',    isBan: false },
  14: { name: 'R Pick 2',  team: 'radiant', isBan: false },
  15: { name: 'R Pick 3',  team: 'radiant', isBan: false },
  16: { name: 'D Pick 3',  team: 'dire',    isBan: false },
  17: { name: 'D Pick 4',  team: 'dire',    isBan: false },
  18: { name: 'R Pick 4',  team: 'radiant', isBan: false },
  19: { name: 'R Ban 6',   team: 'radiant', isBan: true  },
  20: { name: 'D Ban 6',   team: 'dire',    isBan: true  },
  21: { name: 'R Ban 7',   team: 'radiant', isBan: true  },
  22: { name: 'D Ban 7',   team: 'dire',    isBan: true  },
  23: { name: 'R Pick 5',  team: 'radiant', isBan: false },
  24: { name: 'D Pick 5',  team: 'dire',    isBan: false },
};

const Wrap = styled.div`
  min-height: 100vh;
  background: #0d1117;
  color: #e6edf3;
  padding: 24px 16px;
  font-family: 'Roboto', sans-serif;
`;

const Title = styled.h1`
  font-size: 22px;
  font-weight: 700;
  margin: 0 0 4px;
  color: #90caf9;
`;

const Subtitle = styled.p`
  font-size: 13px;
  color: #8b949e;
  margin: 0 0 24px;
`;

const ModeToggle = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
`;

const ModeBtn = styled.button`
  padding: 6px 18px;
  border-radius: 6px;
  border: 1px solid ${p => p.$active ? '#90caf9' : '#30363d'};
  background: ${p => p.$active ? 'rgba(144,202,249,0.15)' : 'transparent'};
  color: ${p => p.$active ? '#90caf9' : '#8b949e'};
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  transition: all 0.15s;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 10px;
`;

const SlotCard = styled.div`
  background: #161b22;
  border: 1px solid ${p => p.$isBan ? '#5a1f1f' : '#1a3a1a'};
  border-radius: 8px;
  padding: 12px;
  text-align: center;
`;

const SlotLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: ${p => p.$isBan ? '#ff6b6b' : (p.$team === 'radiant' ? '#92ff92' : '#ff9999')};
  margin-bottom: 8px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const AvgValue = styled.div`
  font-size: 34px;
  font-weight: 700;
  font-family: 'Century Gothic', 'Futura', 'Trebuchet MS', sans-serif;
  color: #ff6a00;
  line-height: 1;
  text-shadow: 0 0 10px #ff6a00, 0 0 20px rgba(255,106,0,0.5);
`;

const AvgUnit = styled.span`
  font-size: 12px;
  color: #8b949e;
  margin-left: 2px;
`;

const MinMax = styled.div`
  font-size: 10px;
  color: #555;
  margin-top: 4px;
`;

const CountLabel = styled.div`
  font-size: 10px;
  color: #3d4a5c;
  margin-top: 2px;
`;

const NoData = styled.div`
  color: #8b949e;
  font-size: 14px;
  text-align: center;
  padding: 60px 0;
`;

const LoadingMsg = styled(NoData)``;

const SectionHead = styled.h2`
  font-size: 14px;
  color: #8b949e;
  font-weight: 600;
  margin: 20px 0 12px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border-bottom: 1px solid #21262d;
  padding-bottom: 6px;
`;

export default function DraftTimingAnalytics() {
  const { currentUser } = useAuth();
  const [mode, setMode] = useState('first'); // 'first' | 'second'
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const user = currentUser || firebaseService.getCurrentUser();
        if (!user) { setError('Not logged in.'); setLoading(false); return; }
        const agg = await supabaseService.getPickTimingsAggregated(user.uid);
        setData(agg);
      } catch (e) {
        setError(e.message || 'Failed to load timing data.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentUser]);

  const slots = data?.[mode] || {};
  const positions = Object.keys(slots).map(Number).sort((a, b) => a - b);

  return (
    <Wrap>
      <Title>Draft Timing Analytics</Title>
      <Subtitle>
        Average seconds between each selection — based on your saved live drafts.
        {data && ` (${data.totalDrafts} draft${data.totalDrafts !== 1 ? 's' : ''} with timing data)`}
      </Subtitle>

      <ModeToggle>
        <ModeBtn $active={mode === 'first'} onClick={() => setMode('first')}>1st Pick</ModeBtn>
        <ModeBtn $active={mode === 'second'} onClick={() => setMode('second')}>2nd Pick</ModeBtn>
      </ModeToggle>

      {loading && <LoadingMsg>Loading timing data…</LoadingMsg>}
      {error && <NoData style={{ color: '#ff6b6b' }}>{error}</NoData>}

      {!loading && !error && positions.length === 0 && (
        <NoData>
          No timing data yet for {mode === 'first' ? '1st Pick' : '2nd Pick'} drafts.<br />
          Save a live GSI draft to start collecting data.
        </NoData>
      )}

      {!loading && !error && positions.length > 0 && (
        <>
          <SectionHead>Bans</SectionHead>
          <Grid>
            {positions.filter(p => SLOT_META[p]?.isBan).map(pos => {
              const s = slots[pos];
              const meta = SLOT_META[pos] || { name: `Pos ${pos}`, team: 'radiant', isBan: true };
              return (
                <SlotCard key={pos} $isBan>
                  <SlotLabel $isBan $team={meta.team}>{meta.name}</SlotLabel>
                  <AvgValue>{s.avg}<AvgUnit>s</AvgUnit></AvgValue>
                  <MinMax>min {s.min}s · max {s.max}s</MinMax>
                  <CountLabel>{s.count} sample{s.count !== 1 ? 's' : ''}</CountLabel>
                </SlotCard>
              );
            })}
          </Grid>

          <SectionHead>Picks</SectionHead>
          <Grid>
            {positions.filter(p => !SLOT_META[p]?.isBan).map(pos => {
              const s = slots[pos];
              const meta = SLOT_META[pos] || { name: `Pos ${pos}`, team: 'radiant', isBan: false };
              return (
                <SlotCard key={pos}>
                  <SlotLabel $team={meta.team}>{meta.name}</SlotLabel>
                  <AvgValue>{s.avg}<AvgUnit>s</AvgUnit></AvgValue>
                  <MinMax>min {s.min}s · max {s.max}s</MinMax>
                  <CountLabel>{s.count} sample{s.count !== 1 ? 's' : ''}</CountLabel>
                </SlotCard>
              );
            })}
          </Grid>
        </>
      )}
    </Wrap>
  );
}
